import React, { useEffect, useState, useCallback } from "react";
import {
  Modal, Steps, Switch, Spin, Input, InputNumber,
  Select as AntSelect, DatePicker,
} from "antd";
import {
  FileTextOutlined, AlignLeftOutlined, SendOutlined,
  SaveOutlined, CopyOutlined, PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import ReactSelect from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { notify } from "../../../../services/toast.config";
import type { Job } from "../../../../core/data/redux/recruitment/types";
import {
  createJob,
  updateJob,
  publishJob,
  fetchRoundTemplates,
  saveJobRounds,
} from "../../../../core/data/redux/recruitment/thunks";
import {
  selectRoundTemplates,
} from "../../../../core/data/redux/recruitment/selectors";
import {
  EMPLOYMENT_TYPE_OPTIONS,
  WORK_MODE_OPTIONS,
} from "../../constants/recruitment.constants";

type ModalMode = "create" | "edit" | "clone";

interface ScreeningQuestion {
  id: string;
  question: string;
  type: "text" | "yes_no" | "multiple_choice";
  required: boolean;
  options: string[];
}

interface JobFormData {
  job_title: string;
  department_id: number | null;
  work_location: string;
  work_mode: string;
  employment_type: string;
  openings_count: number;
  min_experience_years: number;
  max_experience_years: number;
  min_salary: number | null;
  max_salary: number | null;
  display_salary: boolean;
  currency: string;
  minimum_education: string;
  application_deadline: string | null;
  max_notice_period_days: number | null;
  status: string;
}

interface Props {
  open: boolean;
  mode: ModalMode;
  job: Job | null;
  loading?: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  departmentOptions: { value: number; label: string }[];
}

const EMPTY_FORM: JobFormData = {
  job_title: "",
  department_id: null,
  work_location: "",
  work_mode: "hybrid",
  employment_type: "full-time",
  openings_count: 1,
  min_experience_years: 0,
  max_experience_years: 0,
  min_salary: null,
  max_salary: null,
  display_salary: false,
  currency: "INR",
  minimum_education: "",
  application_deadline: null,
  max_notice_period_days: null,
  status: "draft",
};

const SKILL_OPTIONS = [
  { value: "react", label: "React.js" },
  { value: "node", label: "Node.js" },
  { value: "typescript", label: "TypeScript" },
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "aws", label: "AWS" },
  { value: "docker", label: "Docker" },
  { value: "kubernetes", label: "Kubernetes" },
  { value: "sql", label: "SQL" },
  { value: "mongodb", label: "MongoDB" },
  { value: "agile", label: "Agile Development" },
  { value: "figma", label: "Figma" },
  { value: "graphql", label: "GraphQL" },
  { value: "rest_api", label: "REST APIs" },
];

const EDUCATION_OPTIONS = [
  { value: "high_school", label: "High School / 10+2" },
  { value: "diploma", label: "Diploma" },
  { value: "bachelor", label: "Bachelor's Degree" },
  { value: "master", label: "Master's Degree" },
  { value: "mba", label: "MBA" },
  { value: "phd", label: "PhD / Doctorate" },
  { value: "any", label: "Any" },
];

const CURRENCY_OPTIONS = [
  { value: "INR", label: "₹ INR" },
  { value: "USD", label: "$ USD" },
  { value: "EUR", label: "€ EUR" },
  { value: "GBP", label: "£ GBP" },
  { value: "AED", label: "د.إ AED" },
];

const QUESTION_TYPE_OPTIONS = [
  { value: "text", label: "Text Answer" },
  { value: "yes_no", label: "Yes / No" },
  { value: "multiple_choice", label: "Multiple Choice" },
];

const reactSelectStyle = {
  control: (base: any) => ({
    ...base,
    padding: "2px",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    borderColor: "#e2e8f0",
    boxShadow: "none",
    minHeight: 40,
    "&:hover": { borderColor: "#94a3b8" },
  }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
};

const isQuillEmpty = (v: string) => v.replace(/<[^>]*>/g, "").trim() === "";

const FIELD_STEP_MAP: Record<string, number> = {
  job_title: 0, department_id: 0, work_location: 0, work_mode: 0,
  employment_type: 0, openings_count: 0, min_experience_years: 0,
  max_experience_years: 0, min_salary: 0, max_salary: 0, currency: 0,
  minimum_education: 0, application_deadline: 0, max_notice_period_days: 0,
  display_salary: 0,
  job_summary: 1, responsibilities: 1, detailed_description: 1,
  required_skills: 1, preferred_skills: 1,
  screening_questions: 2,
  interview_rounds: 3,
  status: 4,
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "#64748b",
  fontWeight: 600,
};

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label className="form-label fw-semibold" style={LABEL_STYLE}>
    {children} {required && <span className="text-danger">*</span>}
  </label>
);

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? (
    <div style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>
      <i className="ti ti-alert-circle me-1" />{message}
    </div>
  ) : null;

const JobFormModal: React.FC<Props> = ({
  open, mode, job, loading = false, onCancel, onSuccess, departmentOptions,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);
  const roundTemplates = useSelector(selectRoundTemplates);
  const totalSteps = 5;

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<JobFormData>(EMPTY_FORM);
  const [jobSummary, setJobSummary] = useState("");
  const [responsibilities, setResponsibilities] = useState("");
  const [detailedDescription, setDetailedDescription] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<any[]>([]);
  const [preferredSkills, setPreferredSkills] = useState<any[]>([]);
  const [screeningQuestions, setScreeningQuestions] = useState<ScreeningQuestion[]>([]);
  const [jobRounds, setJobRounds] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  type FormErrorKey =
    | keyof JobFormData
    | "job_summary" | "responsibilities" | "required_skills"
    | "preferred_skills" | "screening_questions" | "general";

  const [errors, setErrors] = useState<Partial<Record<FormErrorKey, string>>>({});

  const [postCareerPage, setPostCareerPage] = useState(true);
  const [postLinkedIn, setPostLinkedIn] = useState(true);
  const [postIndeed, setPostIndeed] = useState(true);
  const [postNaukri, setPostNaukri] = useState(false);

  const deptSelectOptions = departmentOptions.map((d) => ({
    value: Number(d.value),
    label: d.label,
  }));

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setShowSuccess(false);
    setErrors({});
    dispatch(fetchRoundTemplates());

    if ((mode === "edit" || mode === "clone") && job) {
      setFormData({
        job_title: mode === "clone" ? `Copy of ${job.job_title}` : job.job_title || "",
        department_id: job.department_id || null,
        work_location: job.work_location || "",
        work_mode: job.work_mode || "hybrid",
        employment_type: job.employment_type || "full-time",
        openings_count: job.openings_count || 1,
        min_experience_years: job.min_experience_years || 0,
        max_experience_years: job.max_experience_years || 0,
        min_salary: job.min_salary || null,
        max_salary: job.max_salary || null,
        display_salary: (job as any).display_salary || false,
        currency: job.currency || "INR",
        minimum_education: job.minimum_education || "",
        application_deadline: (job as any).application_deadline || null,
        max_notice_period_days: (job as any).max_notice_period_days || null,
        status: job.status || "draft",
      });
      setJobSummary(job.job_summary || "");
      setResponsibilities(
        Array.isArray(job.responsibilities)
          ? job.responsibilities.join("\n")
          : (job.responsibilities || ""),
      );
      setDetailedDescription((job as any).detailed_description || "");
      setSelectedSkills((job.required_skills || []).map((s: string) => ({ value: s, label: s })));
      setPreferredSkills(
        ((job as any).preferred_skills || []).map((s: string) => ({ value: s, label: s })),
      );
      const rawQ = (job as any).screening_questions;
      setScreeningQuestions(
        Array.isArray(rawQ)
          ? rawQ.map((q: any, i: number) =>
            typeof q === "string"
              ? { id: `q_${i}`, question: q, type: "text", required: false, options: [] }
              : {
                id: `q_${i}`,
                question: q.question || "",
                type: q.type || "text",
                required: q.required || false,
                options: q.options || [],
              },
          )
          : [],
      );
      setJobRounds((job.rounds || []).map((r: any) => ({
        round_name: r.round_name || "",
        round_type: r.round_type || "",
        duration_minutes: r.duration_minutes || 60,
        stage_order: r.stage_order || 0,
        criteria_schema: r.criteria_schema || [],
        is_mandatory: r.is_mandatory !== false,
        template_id: r.template_id || null,
      })));
    } else {
      setFormData(EMPTY_FORM);
      setJobSummary("");
      setResponsibilities("");
      setDetailedDescription("");
      setSelectedSkills([]);
      setPreferredSkills([]);
      setScreeningQuestions([]);
      setJobRounds([]);
    }
  }, [open, mode, job, dispatch]);

  const handleFieldChange = useCallback((field: keyof JobFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  }, []);

  const clearQuillError = (key: FormErrorKey) =>
    setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });

  const addScreeningQuestion = () =>
    setScreeningQuestions((prev) => [
      ...prev,
      { id: `q_${Date.now()}`, question: "", type: "text", required: false, options: [] },
    ]);

  const updateQuestion = (id: string, updates: Partial<ScreeningQuestion>) =>
    setScreeningQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, ...updates } : q)));

  const removeQuestion = (id: string) =>
    setScreeningQuestions((prev) => prev.filter((q) => q.id !== id));

  const addOption = (qId: string) =>
    setScreeningQuestions((prev) =>
      prev.map((q) => (q.id === qId ? { ...q, options: [...q.options, ""] } : q)),
    );

  const updateOption = (qId: string, idx: number, val: string) =>
    setScreeningQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? { ...q, options: q.options.map((o, i) => (i === idx ? val : o)) }
          : q,
      ),
    );

  const removeOption = (qId: string, idx: number) =>
    setScreeningQuestions((prev) =>
      prev.map((q) =>
        q.id === qId ? { ...q, options: q.options.filter((_, i) => i !== idx) } : q,
      ),
    );

  const validateStep = (s: number): boolean => {
    const newErrors: typeof errors = {};
    if (s === 0) {
      if (!formData.job_title?.trim()) newErrors.job_title = "Job Title is required.";
      if (!formData.work_location?.trim()) newErrors.work_location = "Work Location is required.";
    }
    if (s === 1) {
      if (isQuillEmpty(jobSummary)) newErrors.job_summary = "Job Summary is required.";
      if (isQuillEmpty(responsibilities)) newErrors.responsibilities = "Responsibilities are required.";
    }
    if (s === 2) {
      const hasEmpty = screeningQuestions.some((q) => !q.question.trim());
      if (hasEmpty) newErrors.screening_questions = "All questions must have text.";
    }
    if (Object.keys(newErrors).length) { setErrors(newErrors); return false; }
    setErrors({});
    return true;
  };

  const handleSubmit = async () => {
    const newErrors: typeof errors = {};
    if (!formData.job_title?.trim()) newErrors.job_title = "Job Title is required.";
    if (!formData.work_location?.trim()) newErrors.work_location = "Work Location is required.";
    if (!activeCompany?.company_id) newErrors.general = "Company context missing, please refresh.";
    if (isQuillEmpty(jobSummary)) newErrors.job_summary = "Job Summary is required.";
    if (isQuillEmpty(responsibilities)) newErrors.responsibilities = "Responsibilities are required.";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    setSubmitting(true);
    try {
      const payload: Record<string, any> = {
        job_title: formData.job_title,
        department_id: formData.department_id,
        work_location: formData.work_location,
        work_mode: formData.work_mode,
        employment_type: formData.employment_type,
        openings_count: formData.openings_count,
        min_experience_years: Number(formData.min_experience_years) || 0,
        max_experience_years: Number(formData.max_experience_years) || 0,
        minimum_education: formData.minimum_education || null,
        required_skills: selectedSkills.map((s) => s.value),
        preferred_skills: preferredSkills.map((s) => s.value),
        job_summary: jobSummary,
        responsibilities,
        detailed_description: isQuillEmpty(detailedDescription) ? null : detailedDescription,
        display_salary: formData.display_salary,
        min_salary: formData.min_salary || null,
        max_salary: formData.max_salary || null,
        currency: formData.currency,
        application_deadline: formData.application_deadline || null,
        max_notice_period_days: formData.max_notice_period_days || null,
        screening_questions:
          screeningQuestions.length > 0
            ? screeningQuestions.map(({ id: _id, ...q }) => q)
            : null,
        status: formData.status,
      };

      if (mode === "edit" && job) {
        await dispatch(updateJob({ jobId: job.id, jobData: payload })).unwrap();
        
        try {
          const cleanedRounds = jobRounds.map((r: any) => ({
            round_name: r.round_name,
            round_type: r.round_type,
            duration_minutes: r.duration_minutes || 60,
            stage_order: r.stage_order,
            criteria_schema: r.criteria_schema || [],
            is_mandatory: r.is_mandatory !== false,
            template_id: r.template_id || null,
          }));
          await dispatch(saveJobRounds({ jobId: job.id, rounds: cleanedRounds })).unwrap();
        } catch (e) {
          console.error("Failed to save job rounds on edit:", e);
        }

        notify.success("Job updated successfully!");
        onSuccess();
        return;
      }

      const result = await dispatch(createJob(payload)).unwrap();
      const newJobId = result?.id || result?.job_id;

      if (jobRounds.length > 0 && newJobId) {
        try {
          const cleanedRounds = jobRounds.map((r: any) => ({
            round_name: r.round_name,
            round_type: r.round_type,
            duration_minutes: r.duration_minutes || 60,
            stage_order: r.stage_order,
            criteria_schema: r.criteria_schema || [],
            is_mandatory: r.is_mandatory !== false,
            template_id: r.template_id || null,
          }));
          await dispatch(saveJobRounds({ jobId: newJobId, rounds: cleanedRounds })).unwrap();
        } catch { }
      }

      const platforms: string[] = [];
      if (postCareerPage) platforms.push("career_page");
      if (postLinkedIn) platforms.push("linkedin");
      if (postIndeed) platforms.push("indeed");
      if (postNaukri) platforms.push("naukri");

      if (platforms.length > 0 && newJobId) {
        try { await dispatch(publishJob({ jobId: newJobId, platforms })).unwrap(); } catch { }
      }

      notify.success(mode === "clone" ? "Job cloned successfully!" : "Job created!");
      setShowSuccess(true);
      setTimeout(() => { onSuccess(); }, 1200);
    } catch (error: any) {
      const details = error?.data?.error?.details ?? error?.error?.details ?? [];
      if (details.length > 0) {
        const mapped: Partial<Record<FormErrorKey, string>> = {};
        let earliest = totalSteps - 1;
        details.forEach(({ field, message }: { field: string; message: string }) => {
          mapped[field as FormErrorKey] = message.replace(/^"[^"]*"\s*/, "");
          const fs = FIELD_STEP_MAP[field] ?? 0;
          if (fs < earliest) earliest = fs;
        });
        setErrors(mapped);
        setStep(earliest);
      } else {
        setErrors({ general: error?.data?.message || error?.message || "Failed to save job" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const modeConfig = {
    create: { title: "Create New Job", icon: <PlusOutlined />, color: "#3b82f6" },
    edit: { title: "Edit Job", icon: <SaveOutlined />, color: "#f59e0b" },
    clone: { title: "Clone Job", icon: <CopyOutlined />, color: "#8b5cf6" },
  };
  const mc = modeConfig[mode];

  if (showSuccess) {
    return (
      <Modal open={open} onCancel={onCancel} footer={null} width={480} centered destroyOnClose
        keyboard={false}
        zIndex={1001}
        styles={{
          mask: {
            position: "fixed", inset: 0, width: "100vw", height: "100vh",
            backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
            background: "rgba(0, 0, 0, 0.45)",
          },
        }}
      >
        <div className="text-center py-5">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ width: 72, height: 72, background: "#ecfdf5", border: "3px solid #d1fae5" }}
          >
            <i className="ti ti-check" style={{ fontSize: 32, color: "#10b981" }} />
          </div>
          <h4 className="fw-bold mb-2" style={{ color: "#1e293b" }}>
            {mode === "clone" ? "Job Cloned!" : "Job Created!"}
          </h4>
          <p className="text-muted">Your job posting is now live.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width="60vw"
      zIndex={1001}
      getContainer={() => document.body}
      style={{ top: "4vh", padding: 0 }}
      styles={{
        content: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "90vh",
          overflow: "hidden",
          borderRadius: 12,
        },
        header: {
          flexShrink: 0,
          padding: "14px 20px 12px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 0,
        },
        body: {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          padding: 0,
          minHeight: 0,
        },
        footer: {
          flexShrink: 0,
          padding: "10px 20px 14px",
          borderTop: "1px solid #f0f0f0",
          marginTop: 0,
        },
        mask: {
          position: "fixed", inset: 0, width: "100vw", height: "100vh",
          backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: mc.color + "18",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: mc.color, fontSize: 15,
            }}
          >
            {mc.icon}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{mc.title}</div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", fontWeight: 400 }}>
              {mode === "edit" ? `Editing: ${job?.job_title || ""}` : "Fill in the details below"}
            </div>
          </div>
        </div>
      }
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered={false}
      footer={[
        <div
          key="footer"
          style={{
            padding: "14px 28px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            className="btn btn-light border px-4"
            style={{ borderRadius: 8 }}
            onClick={() => { if (step > 0) setStep(step - 1); else onCancel(); }}
          >
            <i className="ti ti-arrow-left me-1" />
            {step === 0 ? "Cancel" : "Back"}
          </button>

          {step < totalSteps - 1 ? (
            <button
              className="btn btn-info px-4"
              style={{ borderRadius: 8 }}
              onClick={() => { if (validateStep(step)) setStep(step + 1); }}
            >
              Continue <i className="ti ti-arrow-right ms-1" />
            </button>
          ) : (
            <button
              className="btn px-4"
              style={{
                borderRadius: 8,
                background: mode === "edit" ? "#f59e0b" : "#10b981",
                color: "#fff",
                fontWeight: 600,
              }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {mode === "edit" ? "Saving..." : "Publishing..."}
                </>
              ) : mode === "edit" ? (
                <><SaveOutlined className="me-2" />Save Changes</>
              ) : (
                <><SendOutlined className="me-2" />{mode === "clone" ? "Clone & Publish" : "Publish Job"}</>
              )}
            </button>
          )}
        </div>,
      ]}
    >
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ flex: 1 }}>
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflowY: "auto" }}>
          <div style={{ padding: "16px 24px 12px", flexShrink: 0, borderBottom: "1px solid #f5f5f5" }}>
            <Steps
              current={step}
              size="small"
              items={[
                { title: "Details", icon: <FileTextOutlined /> },
                { title: "Description", icon: <AlignLeftOutlined /> },
                { title: "Screening", icon: <QuestionCircleOutlined /> },
                { title: "Rounds", icon: <i className="ti ti-list-details" style={{ fontSize: 14 }} /> },
                { title: "Publish", icon: <SendOutlined /> },
              ]}
            />
          </div>

          {errors.general && (
            <div
              style={{
                margin: "12px 24px 0",
                padding: "10px 14px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                color: "#dc2626",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <i className="ti ti-alert-triangle" style={{ fontSize: 16 }} />
              {errors.general}
            </div>
          )}

          <div style={{ padding: "16px 24px 20px" }}>


            {step === 0 && (
              <div className="row g-3">
                <div className="col-md-6">
                  <FieldLabel required>Job Title</FieldLabel>
                  <Input
                    size="large"
                    value={formData.job_title}
                    onChange={(e) => handleFieldChange("job_title", e.target.value)}
                    placeholder="e.g. Senior Frontend Engineer"
                    status={errors.job_title ? "error" : ""}
                    style={{ borderRadius: 8, background: "#f8fafc" }}
                  />
                  <FieldError message={errors.job_title} />
                </div>

                <div className="col-md-6">
                  <FieldLabel>Department</FieldLabel>
                  <ReactSelect
                    options={deptSelectOptions}
                    placeholder="Select Department"
                    isClearable
                    value={deptSelectOptions.find((o) => o.value === formData.department_id) || null}
                    onChange={(opt: any) => handleFieldChange("department_id", opt?.value || null)}
                    styles={reactSelectStyle}
                  />
                </div>

                <div className="col-md-6">
                  <FieldLabel required>Work Location</FieldLabel>
                  <Input
                    size="large"
                    prefix={<i className="ti ti-map-pin" style={{ color: errors.work_location ? "#ef4444" : "#94a3b8" }} />}
                    value={formData.work_location}
                    onChange={(e) => handleFieldChange("work_location", e.target.value)}
                    placeholder="e.g. Remote, or Gurugram, Haryana"
                    status={errors.work_location ? "error" : ""}
                    style={{ borderRadius: 8, background: "#f8fafc" }}
                  />
                  <FieldError message={errors.work_location} />
                </div>

                <div className="col-md-6">
                  <FieldLabel>Work Mode</FieldLabel>
                  <ReactSelect
                    options={WORK_MODE_OPTIONS}
                    value={WORK_MODE_OPTIONS.find((o) => o.value === formData.work_mode)}
                    onChange={(opt: any) => handleFieldChange("work_mode", opt?.value || "hybrid")}
                    styles={reactSelectStyle}
                  />
                </div>

                <div className="col-md-4">
                  <FieldLabel>Employment Type</FieldLabel>
                  <ReactSelect
                    options={EMPLOYMENT_TYPE_OPTIONS}
                    value={EMPLOYMENT_TYPE_OPTIONS.find((o) => o.value === formData.employment_type)}
                    onChange={(opt: any) => handleFieldChange("employment_type", opt?.value || "full-time")}
                    styles={reactSelectStyle}
                  />
                </div>

                <div className="col-md-4">
                  <FieldLabel>Min Experience (yrs)</FieldLabel>
                  <InputNumber
                    size="large" min={0}
                    value={formData.min_experience_years}
                    onChange={(v) => handleFieldChange("min_experience_years", v || 0)}
                    style={{ width: "100%", borderRadius: 8, background: "#f8fafc" }}
                  />
                </div>

                <div className="col-md-4">
                  <FieldLabel>Max Experience (yrs)</FieldLabel>
                  <InputNumber
                    size="large" min={0}
                    value={formData.max_experience_years}
                    onChange={(v) => handleFieldChange("max_experience_years", v || 0)}
                    style={{ width: "100%", borderRadius: 8, background: "#f8fafc" }}
                  />
                </div>

                <div className="col-md-4">
                  <FieldLabel>Openings</FieldLabel>
                  <InputNumber
                    size="large" min={1}
                    value={formData.openings_count}
                    onChange={(v) => handleFieldChange("openings_count", v || 1)}
                    style={{ width: "100%", borderRadius: 8, background: "#f8fafc" }}
                  />
                </div>

                <div className="col-md-4">
                  <FieldLabel>Min Notice Period (days)</FieldLabel>
                  <InputNumber
                    size="large" min={0}
                    value={formData.max_notice_period_days}
                    onChange={(v) => handleFieldChange("max_notice_period_days", v)}
                    placeholder="e.g. 30"
                    style={{ width: "100%", borderRadius: 8, background: "#f8fafc" }}
                  />
                </div>

                <div className="col-md-4">
                  <FieldLabel>Application Deadline</FieldLabel>
                  <DatePicker
                    size="large"
                    style={{ width: "100%", borderRadius: 8, background: "#f8fafc" }}
                    value={formData.application_deadline ? dayjs(formData.application_deadline) : null}
                    onChange={(_, dateStr) =>
                      handleFieldChange("application_deadline", dateStr || null)
                    }
                    disabledDate={(d) => d && d.isBefore(dayjs(), "day")}
                    placeholder="Select deadline"
                    allowClear
                  />
                </div>

                <div className="col-md-4">
                  <FieldLabel>Minimum Education</FieldLabel>
                  <ReactSelect
                    options={EDUCATION_OPTIONS}
                    placeholder="Select education level"
                    isClearable
                    value={EDUCATION_OPTIONS.find((o) => o.value === formData.minimum_education) || null}
                    onChange={(opt: any) => handleFieldChange("minimum_education", opt?.value || "")}
                    styles={reactSelectStyle}
                  />
                </div>


                <div className="col-12">
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      padding: "14px 16px",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <span style={{ ...LABEL_STYLE, fontSize: 11.5 }}>
                        <i className="ti ti-currency-rupee me-1" />Salary Range
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 12, color: "#64748b" }}>Display salary to candidates</span>
                        <Switch
                          size="small"
                          checked={formData.display_salary}
                          onChange={(v) => handleFieldChange("display_salary", v)}
                        />
                      </div>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <FieldLabel>Currency</FieldLabel>
                        <AntSelect
                          size="large"
                          style={{ width: "100%" }}
                          value={formData.currency}
                          onChange={(v) => handleFieldChange("currency", v)}
                          options={CURRENCY_OPTIONS}
                        />
                      </div>
                      <div className="col-md-4">
                        <FieldLabel>Min Salary</FieldLabel>
                        <InputNumber
                          size="large" min={0}
                          value={formData.min_salary}
                          onChange={(v) => handleFieldChange("min_salary", v)}
                          formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          placeholder="e.g. 500000"
                          style={{ width: "100%", borderRadius: 8, background: "#fff" }}
                        />
                      </div>
                      <div className="col-md-4">
                        <FieldLabel>Max Salary</FieldLabel>
                        <InputNumber
                          size="large" min={0}
                          value={formData.max_salary}
                          onChange={(v) => handleFieldChange("max_salary", v)}
                          formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          placeholder="e.g. 1000000"
                          style={{ width: "100%", borderRadius: 8, background: "#fff" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}


            {step === 1 && (
              <div>
                <div className="mb-3">
                  <FieldLabel required>Job Summary</FieldLabel>
                  <div
                    style={{
                      border: `1px solid ${errors.job_summary ? "#fca5a5" : "#e2e8f0"}`,
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={jobSummary}
                      onChange={(v) => { setJobSummary(v); clearQuillError("job_summary"); }}
                      style={{ height: 140 }}
                      placeholder="Enter job overview and what makes this role exciting..."
                    />
                  </div>
                  <FieldError message={errors.job_summary} />
                </div>

                <div className="mb-3" style={{ marginTop: 50 }}>
                  <FieldLabel required>Responsibilities</FieldLabel>
                  <div
                    style={{
                      border: `1px solid ${errors.responsibilities ? "#fca5a5" : "#e2e8f0"}`,
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={responsibilities}
                      onChange={(v) => { setResponsibilities(v); clearQuillError("responsibilities"); }}
                      style={{ height: 120 }}
                      placeholder="List the key responsibilities..."
                    />
                  </div>
                  <FieldError message={errors.responsibilities} />
                </div>

                <div className="mb-3" style={{ marginTop: 50 }}>
                  <FieldLabel>Detailed Description</FieldLabel>
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={detailedDescription}
                      onChange={setDetailedDescription}
                      style={{ height: 120 }}
                      placeholder="Additional details about the role, team, perks, etc..."
                    />
                  </div>
                </div>

                <div style={{ marginTop: 50 }}>
                  <div className="row g-3">
                    <div className="col-12">
                      <div
                        style={{
                          background: "#f8fafc",
                          padding: 16,
                          borderRadius: 10,
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <FieldLabel>
                          <i className="ti ti-tags me-1" />Required Skills
                        </FieldLabel>
                        <AntSelect
                          mode="tags"
                          size="large"
                          style={{ width: "100%", marginTop: 6 }}
                          placeholder="Search or type to add a skill..."
                          value={selectedSkills.map((s) => s.value)}
                          onChange={(values: string[]) =>
                            setSelectedSkills(
                              values.map((v) => {
                                const known = SKILL_OPTIONS.find((o) => o.value === v);
                                return known ?? { value: v, label: v };
                              }),
                            )
                          }
                          options={SKILL_OPTIONS}
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          tokenSeparators={[","]}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div
                        style={{
                          background: "#fafff7",
                          padding: 16,
                          borderRadius: 10,
                          border: "1px solid #d1fae5",
                        }}
                      >
                        <FieldLabel>
                          <i className="ti ti-star me-1" />Preferred Skills
                          <span
                            style={{
                              marginLeft: 8,
                              fontWeight: 400,
                              fontSize: 11,
                              color: "#94a3b8",
                              textTransform: "none",
                              letterSpacing: 0,
                            }}
                          >
                            (optional — nice to have)
                          </span>
                        </FieldLabel>
                        <AntSelect
                          mode="tags"
                          size="large"
                          style={{ width: "100%", marginTop: 6 }}
                          placeholder="Add preferred / bonus skills..."
                          value={preferredSkills.map((s) => s.value)}
                          onChange={(values: string[]) =>
                            setPreferredSkills(
                              values.map((v) => {
                                const known = SKILL_OPTIONS.find((o) => o.value === v);
                                return known ?? { value: v, label: v };
                              }),
                            )
                          }
                          options={SKILL_OPTIONS}
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                          tokenSeparators={[","]}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 6 }}>
                    <i className="ti ti-info-circle me-1" />
                    Select from suggestions or type a custom skill and press{" "}
                    <kbd
                      style={{
                        padding: "1px 5px",
                        borderRadius: 4,
                        background: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        fontSize: 11,
                      }}
                    >
                      Enter
                    </kbd>{" "}
                    to add.
                  </div>
                </div>
              </div>
            )}


            {step === 2 && (
              <div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h6 className="mb-0 fw-semibold" style={{ fontSize: 14, color: "#0f172a" }}>
                      Screening Questions
                    </h6>
                    <p className="mb-0 text-muted" style={{ fontSize: 12.5 }}>
                      Add questions candidates must answer when applying. Optional.
                    </p>
                  </div>
                  <button
                    className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                    style={{ borderRadius: 8 }}
                    onClick={addScreeningQuestion}
                  >
                    <PlusOutlined style={{ fontSize: 13 }} />
                    Add Question
                  </button>
                </div>

                {errors.screening_questions && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: "8px 12px",
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: 8,
                      color: "#dc2626",
                      fontSize: 12.5,
                    }}
                  >
                    <i className="ti ti-alert-triangle me-1" />{errors.screening_questions}
                  </div>
                )}

                {screeningQuestions.length === 0 ? (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      minHeight: 260,
                      background: "#f8fafc",
                      border: "2px dashed #e2e8f0",
                      borderRadius: 12,
                    }}
                  >
                    <i
                      className="ti ti-help-circle"
                      style={{ fontSize: "3rem", color: "#cbd5e1", marginBottom: 12 }}
                    />
                    <p className="mb-1 fw-semibold" style={{ color: "#64748b" }}>
                      No screening questions yet
                    </p>
                    <p className="mb-3 text-muted" style={{ fontSize: 12.5 }}>
                      Click "Add Question" to create your first question.
                    </p>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      style={{ borderRadius: 8 }}
                      onClick={addScreeningQuestion}
                    >
                      <PlusOutlined className="me-1" />Add Question
                    </button>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {screeningQuestions.map((q, idx) => (
                      <div
                        key={q.id}
                        style={{
                          background: "#fff",
                          border: "1px solid #e2e8f0",
                          borderRadius: 12,
                          padding: "16px 18px",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 6,
                              background: "#eff6ff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#3b82f6",
                              flexShrink: 0,
                            }}
                          >
                            {idx + 1}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", flex: 1 }}>
                            Question {idx + 1}
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ fontSize: 12, color: "#64748b" }}>Required</span>
                            <Switch
                              size="small"
                              checked={q.required}
                              onChange={(v) => updateQuestion(q.id, { required: v })}
                            />
                          </div>
                          <button
                            className="btn btn-sm btn-light"
                            style={{
                              borderRadius: 6,
                              padding: "3px 8px",
                              color: "#ef4444",
                              border: "1px solid #fecaca",
                            }}
                            onClick={() => removeQuestion(q.id)}
                          >
                            <i className="ti ti-trash" style={{ fontSize: 13 }} />
                          </button>
                        </div>

                        <div className="row g-2">
                          <div className="col-md-8">
                            <Input
                              size="middle"
                              value={q.question}
                              onChange={(e) => updateQuestion(q.id, { question: e.target.value })}
                              placeholder="e.g. Do you have experience with React?"
                              style={{ borderRadius: 8, background: "#f8fafc" }}
                              status={!q.question.trim() && errors.screening_questions ? "error" : ""}
                            />
                          </div>
                          <div className="col-md-4">
                            <AntSelect
                              size="middle"
                              style={{ width: "100%" }}
                              value={q.type}
                              onChange={(v) =>
                                updateQuestion(q.id, {
                                  type: v as ScreeningQuestion["type"],
                                  options: v === "multiple_choice" ? ["", ""] : [],
                                })
                              }
                              options={QUESTION_TYPE_OPTIONS}
                            />
                          </div>
                        </div>

                        {q.type === "multiple_choice" && (
                          <div className="mt-3">
                            <div
                              style={{
                                fontSize: 11.5,
                                color: "#64748b",
                                fontWeight: 600,
                                marginBottom: 8,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                              }}
                            >
                              Answer Options
                            </div>
                            <div className="d-flex flex-column gap-2">
                              {q.options.map((opt, oIdx) => (
                                <div key={oIdx} className="d-flex align-items-center gap-2">
                                  <span
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      background: "#f1f5f9",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: "#64748b",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {String.fromCharCode(65 + oIdx)}
                                  </span>
                                  <Input
                                    size="small"
                                    value={opt}
                                    onChange={(e) => updateOption(q.id, oIdx, e.target.value)}
                                    placeholder={`Option ${oIdx + 1}`}
                                    style={{ flex: 1, borderRadius: 6, background: "#f8fafc" }}
                                  />
                                  <button
                                    className="btn btn-sm"
                                    style={{
                                      padding: "2px 7px",
                                      color: "#94a3b8",
                                      border: "1px solid #e2e8f0",
                                      borderRadius: 6,
                                    }}
                                    onClick={() => removeOption(q.id, oIdx)}
                                    disabled={q.options.length <= 2}
                                  >
                                    <i className="ti ti-x" style={{ fontSize: 11 }} />
                                  </button>
                                </div>
                              ))}
                              <button
                                className="btn btn-sm btn-outline-secondary align-self-start"
                                style={{ borderRadius: 6, marginTop: 2, fontSize: 12 }}
                                onClick={() => addOption(q.id)}
                                disabled={q.options.length >= 6}
                              >
                                <PlusOutlined style={{ fontSize: 11 }} /> Add Option
                              </button>
                            </div>
                          </div>
                        )}

                        {q.type === "yes_no" && (
                          <div className="mt-2 d-flex gap-2">
                            {["Yes", "No"].map((label) => (
                              <span
                                key={label}
                                style={{
                                  padding: "3px 14px",
                                  borderRadius: 20,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  background: label === "Yes" ? "#ecfdf5" : "#fef2f2",
                                  color: label === "Yes" ? "#059669" : "#dc2626",
                                  border: `1px solid ${label === "Yes" ? "#bbf7d0" : "#fecaca"}`,
                                }}
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      className="btn btn-outline-primary btn-sm align-self-start"
                      style={{ borderRadius: 8 }}
                      onClick={addScreeningQuestion}
                    >
                      <PlusOutlined className="me-1" />Add Another Question
                    </button>
                  </div>
                )}
              </div>
            )}


            {step === 3 && (
              <div>
                <div className="mb-3">
                  <div
                    style={{
                      padding: "14px 18px",
                      background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
                      borderRadius: 10,
                      border: "1px solid #dbeafe",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <i className="ti ti-info-circle" style={{ fontSize: 18, color: "#3b82f6" }} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#1e40af" }}>
                        Interview Rounds (Optional)
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        Configure interview rounds for this job, or skip this step entirely.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick-pick from templates */}
                {roundTemplates.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={LABEL_STYLE}>Use a Template</label>
                    <AntSelect
                      style={{ width: "100%" }}
                      placeholder="Select a round template to pre-fill..."
                      allowClear
                      onChange={(templateId: number) => {
                        const tpl = roundTemplates.find((t: any) => t.id === templateId);
                        if (tpl && (tpl as any).stages) {
                          setJobRounds(
                            (tpl as any).stages.map((s: any, i: number) => ({
                              round_number: i + 1,
                              round_name: s.round_name,
                              round_type: s.round_type,
                              duration_minutes: s.duration_minutes || 60,
                              is_mandatory: s.is_mandatory !== false,
                            })),
                          );
                        }
                      }}
                      options={roundTemplates.map((t: any) => ({
                        value: t.id,
                        label: `${t.template_name || t.name} (${(t.stages || []).length} rounds)`,
                      }))}
                    />
                  </div>
                )}

                {/* Rounds list */}
                <div className="mb-2 d-flex align-items-center justify-content-between">
                  <label className="form-label fw-semibold mb-0" style={LABEL_STYLE}>
                    Rounds ({jobRounds.length})
                  </label>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    style={{ borderRadius: 6, fontSize: 12 }}
                    onClick={() => {
                      setJobRounds((prev) => [
                        ...prev,
                        {
                          round_number: prev.length + 1,
                          round_name: "",
                          round_type: "screening",
                          duration_minutes: 60,
                          is_mandatory: true,
                        },
                      ]);
                    }}
                  >
                    <i className="ti ti-plus me-1" /> Add Round
                  </button>
                </div>

                {jobRounds.length === 0 ? (
                  <div
                    style={{
                      padding: "30px 20px",
                      border: "2px dashed #e2e8f0",
                      borderRadius: 10,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    <i className="ti ti-list-details" style={{ fontSize: 28, display: "block", marginBottom: 8 }} />
                    <div style={{ fontSize: 13, fontWeight: 500 }}>No rounds configured</div>
                    <div style={{ fontSize: 12 }}>You can skip this step or add rounds above.</div>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {jobRounds.map((round: any, idx: number) => (
                      <div
                        key={idx}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "12px 14px",
                          background: "#fafbfc",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 6,
                              background: "#eff6ff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#3b82f6",
                              flexShrink: 0,
                            }}
                          >
                            {idx + 1}
                          </span>
                          <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>
                            Round {idx + 1}
                          </span>
                          <button
                            className="btn btn-sm"
                            style={{ padding: "1px 6px", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 5 }}
                            onClick={() => {
                              setJobRounds((prev) =>
                                prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, round_number: i + 1 })),
                              );
                            }}
                          >
                            <i className="ti ti-trash" style={{ fontSize: 12 }} />
                          </button>
                        </div>
                        <div className="row g-2">
                          <div className="col-md-5">
                            <Input
                              size="small"
                              value={round.round_name}
                              onChange={(e) => {
                                setJobRounds((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, round_name: e.target.value } : r)),
                                );
                              }}
                              placeholder="Round name"
                            />
                          </div>
                          <div className="col-md-4">
                            <AntSelect
                              size="small"
                              style={{ width: "100%" }}
                              value={round.round_type}
                              onChange={(v) => {
                                setJobRounds((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, round_type: v } : r)),
                                );
                              }}
                              options={[
                                { value: "screening", label: "Screening" },
                                { value: "technical_round", label: "Technical" },
                                { value: "coding_test", label: "Coding Test" },
                                { value: "hr_round", label: "HR Round" },
                                { value: "cultural_fit", label: "Cultural Fit" },
                                { value: "managerial_round", label: "Managerial" },
                                { value: "case_study", label: "Case Study" },
                                { value: "final_round", label: "Final Round" },
                                { value: "assignment", label: "Assignment" },
                              ]}
                            />
                          </div>
                          <div className="col-md-3">
                            <InputNumber
                              size="small"
                              style={{ width: "100%" }}
                              value={round.duration_minutes}
                              onChange={(v) => {
                                setJobRounds((prev) =>
                                  prev.map((r, i) => (i === idx ? { ...r, duration_minutes: v || 60 } : r)),
                                );
                              }}
                              min={15}
                              max={480}
                              addonAfter="min"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}


            {step === 4 && (
              <div>
                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2 d-block" style={LABEL_STYLE}>
                    Job Status
                  </label>
                  <div className="d-flex gap-2">
                    {(mode === "edit"
                      ? [
                        { value: "draft", label: "Draft", icon: "ti-pencil", color: "#f59e0b", desc: "Not visible to applicants" },
                        { value: "active", label: "Active", icon: "ti-circle-check", color: "#10b981", desc: "Live and accepting applications" },
                        { value: "closed", label: "Closed", icon: "ti-lock", color: "#64748b", desc: "No longer accepting applications" },
                      ]
                      : [
                        { value: "draft", label: "Draft", icon: "ti-pencil", color: "#f59e0b", desc: "Save without publishing" },
                        { value: "active", label: "Active", icon: "ti-circle-check", color: "#10b981", desc: "Publish immediately" },
                        { value: "inactive", label: "Inactive", icon: "ti-eye-off", color: "#94a3b8", desc: "Hidden from job boards" },
                      ]
                    ).map((s) => (
                      <div
                        key={s.value}
                        onClick={() => handleFieldChange("status", s.value)}
                        style={{
                          flex: 1,
                          border: `1.5px solid ${formData.status === s.value ? s.color : "#e2e8f0"}`,
                          borderRadius: 10,
                          padding: "12px 14px",
                          background: formData.status === s.value ? s.color + "0f" : "#fafafa",
                          cursor: "pointer",
                          transition: "all 0.18s",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: formData.status === s.value ? s.color + "20" : "#f1f5f9",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          <i
                            className={`ti ${s.icon}`}
                            style={{ fontSize: 16, color: formData.status === s.value ? s.color : "#94a3b8" }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: formData.status === s.value ? s.color : "#1e293b" }}>
                            {s.label}
                          </div>
                          <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>{s.desc}</div>
                        </div>
                        {formData.status === s.value && (
                          <i className="ti ti-check" style={{ fontSize: 14, color: s.color, alignSelf: "center" }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="form-label fw-semibold mb-0" style={LABEL_STYLE}>
                    Publish To
                  </label>
                  {formData.status !== "active" && (
                    <span
                      style={{
                        fontSize: 11.5,
                        color: "#f59e0b",
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: 6,
                        padding: "2px 8px",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <i className="ti ti-info-circle" style={{ fontSize: 13 }} />
                      {formData.status === "inactive"
                        ? "Inactive jobs appear in CRM but not on career pages or job boards"
                        : "Set status to Active to publish to job boards"}
                    </span>
                  )}
                </div>

                <div
                  className="row g-3"
                  style={{
                    opacity: formData.status === "active" ? 1 : 0.45,
                    pointerEvents: formData.status === "active" ? "auto" : "none",
                    transition: "opacity 0.2s",
                  }}
                >
                  {[
                    { key: "career_page", name: "Career Page", sub: "careers.yourcompany.com", icon: "ti-world", color: "#10b981", checked: postCareerPage, setter: setPostCareerPage, disabled: false },
                    { key: "linkedin", name: "LinkedIn", sub: "API Connected", icon: "ti-brand-linkedin", color: "#0a66c2", checked: postLinkedIn, setter: setPostLinkedIn, disabled: false },
                    { key: "indeed", name: "Indeed", sub: "API Connected", icon: "ti-briefcase", color: "#2557a7", checked: postIndeed, setter: setPostIndeed, disabled: false },
                    { key: "naukri", name: "Naukri", sub: "Missing Auth Token", icon: "ti-briefcase", color: "#64748b", checked: postNaukri, setter: setPostNaukri, disabled: true },
                  ].map((platform) => (
                    <div key={platform.key} className="col-md-6">
                      <div
                        style={{
                          border: `1.5px solid ${platform.checked && !platform.disabled && formData.status === "active" ? platform.color + "40" : "#e2e8f0"}`,
                          borderRadius: 12,
                          padding: "16px 18px",
                          background: platform.checked && !platform.disabled && formData.status === "active" ? platform.color + "08" : "#fafafa",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          opacity: platform.disabled ? 0.55 : 1,
                          transition: "all 0.2s",
                        }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: 42, height: 42, borderRadius: 10,
                              background: platform.checked && !platform.disabled && formData.status === "active" ? platform.color + "15" : "#f1f5f9",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                          >
                            <i
                              className={`ti ${platform.icon}`}
                              style={{ fontSize: 20, color: platform.checked && formData.status === "active" ? platform.color : "#94a3b8" }}
                            />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{platform.name}</div>
                            <div style={{ fontSize: 11.5, color: platform.disabled ? "#ef4444" : "#64748b" }}>{platform.sub}</div>
                          </div>
                        </div>
                        <Switch
                          checked={platform.checked}
                          onChange={platform.setter}
                          disabled={platform.disabled || formData.status !== "active"}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default JobFormModal;